const { objectToUrlParams, getCookie, sendPreflight, addSwatchesToProducts, filterProductsByEarlyDrop } = window.ssUtils;

document.addEventListener('DOMContentLoaded', async function() {
    const SITE_ID = window.ssSiteId;
    const pageLoadId = window.ssPageLoadId;

    const userId = getCookie('ssUserId') || '';
    const sessionId = getCookie('ssSessionIdNamespace') || '';

    const defaultParams = {
        siteId: SITE_ID,
        resultsFormat: 'json',
        resultsPerPage: 100,
        page: 1,
        userId,
        sessionId,
        pageLoadId,
        domain: window.location.href,
        redirectResponse: 'minimal'
    };

    // Global state for merged facets and filter management
    let mergedFacets = {};
    let allCollectionData = [];

    // Initialize the component
    initializeVisualCollectionFilters();

    function initializeVisualCollectionFilters() {
        const navBoxes = document.querySelectorAll('.collection-nav-box');

        // Add click event listeners to navigation boxes
        navBoxes.forEach(navBox => {
            navBox.addEventListener('click', function() {
                const targetIndex = this.getAttribute('data-collection-index');

                // Update active states
                navBoxes.forEach(box => box.classList.remove('active'));
                this.classList.add('active');

                // Scroll to the target section
                const targetSection = document.getElementById(`collection-section-${targetIndex}`);
                if (targetSection) {
                    targetSection.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            });
        });

        // Set initial filter checkboxes based on URL
        setInitialFilters();

        // Load products for all collections immediately
        loadAllCollectionProducts();

        // Set up intersection observer to update active nav based on scroll position
        setupScrollSpy();

        // Set up filter event listeners
        setupFilterEventListeners();

        // Update filter chips on initial load
        updateFilterChips();
    }

    function setupFilterEventListeners() {
        // Select all fieldsets
        const fieldsets = document.querySelectorAll('.search-filters-panel-body fieldset');

        // Add event listeners to each fieldset
        fieldsets.forEach(fieldset => {
            fieldset.addEventListener('click', function(event) {
                // Check if the clicked element is a checkbox
                if (event.target.type === 'checkbox') {
                    // Get the filter name from the data attribute
                    const filterName = this.getAttribute('data-filter-name');
                    // Check if this is a range filter
                    const isRangeFilter = this.hasAttribute('data-range-filter');

                    if (isRangeFilter) {
                        // For range filters, collect objects with low/high values
                        const checkedBoxes = this.querySelectorAll(`input[name="${filterName}"]:checked`);
                        const selectedRanges = Array.from(checkedBoxes).map(checkbox => {
                            // Get the range data from the checkbox
                            return {
                                low: checkbox.getAttribute('data-low'),
                                high: checkbox.getAttribute('data-high')
                            };
                        });

                        updateFilters(filterName, selectedRanges, true);
                    } else {
                        // Regular filter handling
                        const checkedBoxes = this.querySelectorAll(`input[name="${filterName}"]:checked`);
                        const selectedValues = Array.from(checkedBoxes).map(checkbox => checkbox.value);

                        updateFilters(filterName, selectedValues, false);
                    }
                }
            });
        });

        // Add event listener to the clear filters button
        const clearFiltersButton = document.querySelector('.clear-filters-button');
        if (clearFiltersButton) {
            clearFiltersButton.addEventListener('click', clearFilters);
        }
    }

    function updateFilters(filterName, selectedValues, isRangeFilter = false) {
        const url = new URL(window.location.href);
        const searchParams = url.searchParams;

        let filtersParam = searchParams.get('filters') || '';
        let filterArray = filtersParam ? filtersParam.split(',') : [];

        if (isRangeFilter) {
            // Handle range filters differently
            // Remove any existing range filters for this filter name
            filterArray = filterArray.filter(filter =>
                !filter.startsWith(`filter.${filterName}.low:`) &&
                !filter.startsWith(`filter.${filterName}.high:`)
            );

            // Add new range filters if values are selected
            if (selectedValues.length > 0) {
                // For range filters, we expect selectedValues to be an array of objects with low and high properties
                selectedValues.forEach(range => {
                    if (range.low !== undefined) {
                        filterArray.push(`filter.${filterName}.low:${range.low}`);
                    }
                    if (range.high !== undefined) {
                        filterArray.push(`filter.${filterName}.high:${range.high}`);
                    }
                });
            }
        } else {
            // Original code for regular filters
            filterArray = filterArray.filter(filter => !filter.startsWith(`filter.${filterName}:`));

            if (selectedValues.length > 0) {
                const valueString = selectedValues.join('|');
                filterArray.push(`filter.${filterName}:${valueString}`);
            }
        }

        // Update the URL search parameter
        if (filterArray.length > 0) {
            searchParams.set('filters', filterArray.join(','));
        } else {
            searchParams.delete('filters');
        }

        window.history.replaceState({}, '', url.toString());

        updateFilterChips();

        // Reload all collections with new filters
        reloadAllCollections();
    }

    function reloadAllCollections() {
        // Clear all product grids
        const productGrids = document.querySelectorAll('[id^="visual-collection-"]');
        productGrids.forEach(grid => {
            grid.innerHTML = '';
        });

        // Clear merged facets
        mergedFacets = {};
        allCollectionData = [];

        // Reload all collections
        loadAllCollectionProducts();
    }

    async function loadAllCollectionProducts() {
        const contentSections = document.querySelectorAll('.collection-content-section');

        // Load all collections in parallel
        const loadPromises = Array.from(contentSections).map(section => {
            const collectionHandle = section.getAttribute('data-collection-handle');
            const collectionIndex = section.getAttribute('data-collection-index');
            return loadCollectionProducts(collectionHandle, collectionIndex);
        });

        await Promise.all(loadPromises);

        // After all collections are loaded, merge facets and render filters
        mergeAndRenderFacets();

        // Update filter chips after everything is loaded
        updateFilterChips();
    }

    function mergeAndRenderFacets() {
        // Merge facets from all collections
        allCollectionData.forEach(data => {
            if (data.facets) {
                data.facets.forEach(facet => {
                    if (!mergedFacets[facet.field]) {
                        mergedFacets[facet.field] = {
                            field: facet.field,
                            values: []
                        };
                    }

                    // Merge values for this facet
                    facet.values.forEach(value => {
                        const existingValue = mergedFacets[facet.field].values.find(v => {
                            if (value.type === 'range') {
                                return v.low === value.low && v.high === value.high;
                            } else {
                                return v.value === value.value;
                            }
                        });

                        if (existingValue) {
                            existingValue.count = (existingValue.count || 0) + (value.count || 0);
                        } else {
                            mergedFacets[facet.field].values.push({ ...value });
                        }
                    });
                });
            }
        });

        // Sort size values in specific order if present
        const sizeFacet = mergedFacets['ss_sizes'];
        if (sizeFacet && sizeFacet.values && sizeFacet.values.length > 0) {
            const sizeOrder = {
                'YS': 0,
                'YM': 1,
                'YL': 2,
                'YXL': 3,
                'S': 4,
                'M': 5,
                'L': 6,
                'XL': 7,
                '2XL': 8,
                '3XL': 9
            };

            sizeFacet.values.sort((a, b) => {
                const labelA = a.label || '';
                const labelB = b.label || '';

                // If both labels are in our order map, sort by the order
                if (labelA in sizeOrder && labelB in sizeOrder) {
                    return sizeOrder[labelA] - sizeOrder[labelB];
                }
                // If only one is in the order map, prioritize it
                else if (labelA in sizeOrder) {
                    return -1;
                }
                else if (labelB in sizeOrder) {
                    return 1;
                }
                // Otherwise, use alphabetical sorting
                return labelA.localeCompare(labelB);
            });
        }

        // Render merged facets
        Object.values(mergedFacets).forEach(facet => {
            renderFilterFields(facet);
        });

        // Update filter counts
        updateFilterCounts(Object.values(mergedFacets));
    }

    function renderFilterFields(facet) {
        const fieldset = document.querySelector(`[data-filter-name="${facet.field}"]`);
        if (!fieldset) return;

        fieldset.innerHTML = '';

        if (facet.field === 'tags') {
            facet.values = facet.values.filter(value => value.value === 'adult' || value.value === 'youth');
        }

        // We'll determine if this is a range fieldset by checking the first value
        let isRangeFieldset = false;
        if (facet.values && facet.values.length > 0 && facet.values[0].type === 'range') {
            isRangeFieldset = true;
            fieldset.setAttribute('data-range-filter', 'true');
        }

        const valuesToRender = facet.values || [];

        valuesToRender.forEach((value, index) => {
            const container = document.createElement('div');
            container.classList.add('flex-1', 'flex', 'align-center');

            if (facet.field === 'ss_sizes') {
                if (index < valuesToRender.length / 2) {
                    container.classList.add('column-1');
                } else {
                    container.classList.add('column-2');
                }
            }

            const input = document.createElement('input');
            input.type = 'checkbox';
            input.name = facet.field;

            // Individual value can be range type
            const isRangeValue = value.type === 'range';

            // Set ID appropriately based on value type
            if (isRangeValue) {
                input.id = `${facet.field}-range-${index}`;
            } else {
                input.id = value.value;
            }

            // For range values, add data attributes
            if (isRangeValue) {
                // For range values, check for low/high properties
                if ('low' in value) input.setAttribute('data-low', value.low);
                if ('high' in value) input.setAttribute('data-high', value.high);
                // Used for facet identification
                input.value = `range-${index}`;
            } else {
                input.value = value.value;
            }

            const label = document.createElement('label');
            label.classList.add('flex', 'align-center', 'capitalize');
            label.htmlFor = input.id;
            label.textContent = value.label || value.value;

            const count = document.createElement('small');
            count.classList.add('count');
            count.textContent = value.count !== undefined ? `(${value.count})` : '';

            container.appendChild(input);
            container.appendChild(label);
            label.appendChild(count);

            fieldset.appendChild(container);
        });

        setInitialFilters();
    }

    function updateFilterCounts(facets) {
        const counts = document.querySelectorAll('.search-filters-panel-body .count');
        counts.forEach(count => {
            count.textContent = '';
        });

        facets.forEach(facet => {
            const facetFieldset = document.querySelector(`[data-filter-name="${facet.field}"]`);
            if (facetFieldset) {
                // Check if this is a range fieldset
                const isRangeFieldset = facetFieldset.hasAttribute('data-range-filter');

                facet.values.forEach((value, index) => {
                    // Different handling based on value type
                    const isRangeValue = value.type === 'range';

                    if (isRangeValue || isRangeFieldset) {
                        // For range values/fieldsets, use the index-based ID
                        const rangeId = `${facet.field}-range-${index}`;
                        const label = facetFieldset.querySelector(`label[for="${rangeId}"]`);

                        if (label) {
                            const countEl = label.querySelector('.count');
                            if (countEl) {
                                countEl.textContent = `(${value.count || 0})`;
                            }
                        }
                    } else {
                        // For regular values, use value as ID
                        // Make sure value.value exists
                        if (!value.value) return;

                        // Remove double quotes if they exist around the value
                        const cleanValue = value.value.replace(/^"(.*)"$/, '$1');
                        const label = facetFieldset.querySelector(`label[for="${cleanValue}"]`);

                        if (label) {
                            const countEl = label.querySelector('.count');
                            if (countEl) {
                                countEl.textContent = `(${value.count || 0})`;
                            }
                        }
                    }
                });
            }
        });
    }

    function setInitialFilters() {
        const urlParams = new URLSearchParams(window.location.search);
        const filters = urlParams.get('filters') || '';

        if (!filters) return; // No filters to apply

        // Parse the filters string into a more usable structure
        const filterMap = {};
        const rangeFilterMap = {};
        const filtersArray = filters.split(',');

        filtersArray.forEach(filter => {
            // Make sure the filter has both key and value parts
            const parts = filter.split(':');
            if (parts.length !== 2) return; // Skip malformed filters

            const [key, value] = parts;

            // Skip if key is undefined or empty
            if (!key) return;

            // Check if this is a range filter
            if (key.includes('.low') || key.includes('.high')) {
                // Extract the base filter name and range type
                // Make sure to handle potential undefined values
                const keyParts = key.replace('filter.', '').split('.');
                if (keyParts.length !== 2) return; // Skip malformed range filters

                const filterName = keyParts[0];
                const rangeType = keyParts[1]; // 'low' or 'high'

                // Skip if filterName or rangeType is empty
                if (!filterName || !rangeType) return;

                // Initialize the range filter map entry if needed
                if (!rangeFilterMap[filterName]) {
                    rangeFilterMap[filterName] = [];
                }

                // Store the range value in an array
                // Find an existing range with this type or add a new one
                let found = false;
                for (const range of rangeFilterMap[filterName]) {
                    if (!range[rangeType]) {
                        range[rangeType] = value;
                        found = true;
                        break;
                    }
                }

                // If we didn't find a place to add this value, create a new range object
                if (!found) {
                    const newRange = {};
                    newRange[rangeType] = value;
                    rangeFilterMap[filterName].push(newRange);
                }
            } else {
                // Regular filter
                const filterName = key.replace('filter.', '');
                if (!filterName) return; // Skip if filterName is empty

                const values = value.split('|');
                filterMap[filterName] = values;
            }
        });

        // Find all filter fieldsets
        const fieldsets = document.querySelectorAll('.search-filters-panel-body fieldset');

        fieldsets.forEach(fieldset => {
            const filterName = fieldset.getAttribute('data-filter-name');
            if (!filterName) return; // Skip if no filter name

            const isRangeFilter = fieldset.hasAttribute('data-range-filter');

            if (isRangeFilter && rangeFilterMap[filterName]) {
                // Range filter handling
                const rangeData = rangeFilterMap[filterName];

                // Find all range checkboxes
                const checkboxes = fieldset.querySelectorAll(`input[name="${filterName}"]`);

                checkboxes.forEach(checkbox => {
                    const checkboxLow = checkbox.getAttribute('data-low');
                    const checkboxHigh = checkbox.getAttribute('data-high');

                    // Check if any of our range filters match this checkbox
                    const isChecked = rangeData.some(range => {
                        // Check various matching conditions
                        return (
                            // Both low and high match
                            (range.low === checkboxLow && range.high === checkboxHigh) ||
                            // Only low matches (and checkbox has low)
                            (range.low === checkboxLow && !range.high && checkboxLow) ||
                            // Only high matches (and checkbox has high)
                            (range.high === checkboxHigh && !range.low && checkboxHigh) ||
                            // Only checkbox has low, and it matches range low
                            (checkboxLow && !checkboxHigh && range.low === checkboxLow) ||
                            // Only checkbox has high, and it matches range high
                            (checkboxHigh && !checkboxLow && range.high === checkboxHigh)
                        );
                    });

                    if (isChecked) {
                        checkbox.checked = true;
                    }
                });
            } else if (filterMap[filterName]) {
                // Regular filter handling
                const filterValues = filterMap[filterName];
                const checkboxes = fieldset.querySelectorAll(`input[name="${filterName}"]`);

                checkboxes.forEach(checkbox => {
                    if (filterValues.includes(checkbox.value)) {
                        checkbox.checked = true;
                    }
                });
            }
        });
    }

    function clearFilters() {
        // Check if there is a filters search param in the URL
        const url = new URL(window.location.href);
        const searchParams = url.searchParams;

        // Uncheck all filter checkboxes
        const filterCheckboxes = document.querySelectorAll('.search-filters-panel-body input[type="checkbox"]');
        filterCheckboxes.forEach(checkbox => {
            checkbox.checked = false;
        });

        if (!searchParams.has('filters')) {
            return; // No filters to clear, so return early
        }

        searchParams.delete('filters');
        window.history.replaceState({}, '', url.toString());

        // Update filter chips immediately
        updateFilterChips();

        reloadAllCollections();
    }

    function updateFilterChips() {
        const container = document.getElementById('ss-search-filter-chips');
        if (!container) return;

        // Clear existing chips
        container.innerHTML = '';

        // Get current filters from URL
        const urlParams = new URLSearchParams(window.location.search);
        const filters = urlParams.get('filters') || '';
        if (!filters) return; // No filters to display

        // Split into individual filters
        const filterArray = filters.split(',');

        // Group filters by name for regular filters
        const regularFilters = {};

        // Group range filters into complete pairs only
        const rangePairs = [];
        const tempRanges = {};

        filterArray.forEach(filter => {
            const parts = filter.split(':');
            if (parts.length !== 2) return;

            const key = parts[0];
            const value = parts[1];

            // Check if it's a range filter
            if (key.includes('.low') || key.includes('.high')) {
                const baseName = key.replace('filter.', '').split('.')[0];
                const rangeType = key.includes('.low') ? 'low' : 'high';

                if (!tempRanges[baseName]) {
                    tempRanges[baseName] = [];
                }

                // Try to find a matching pair
                let matchFound = false;

                for (const rangeObj of tempRanges[baseName]) {
                    // If this object doesn't have this property yet
                    if (!rangeObj[rangeType] && !rangeObj.complete) {
                        // Add this range value
                        rangeObj[rangeType] = value;
                        rangeObj.filters.push(filter);
                        matchFound = true;

                        // If we now have both values, it's a complete pair
                        if (rangeObj.low && rangeObj.high) {
                            rangePairs.push({
                                name: baseName,
                                low: rangeObj.low,
                                high: rangeObj.high,
                                filters: [...rangeObj.filters]
                            });
                            rangeObj.complete = true;
                        }
                        break;
                    }
                }

                // If no match found, create a new range object
                if (!matchFound) {
                    tempRanges[baseName].push({
                        [rangeType]: value,
                        filters: [filter],
                        complete: false
                    });
                }
            } else {
                // Regular filter
                const baseName = key.replace('filter.', '');

                if (!regularFilters[baseName]) {
                    regularFilters[baseName] = { values: [], filters: [] };
                }

                // Add pipe-separated values
                value.split('|').forEach(v => {
                    regularFilters[baseName].values.push(v);
                });

                regularFilters[baseName].filters.push(filter);
            }
        });

        // Create chips for regular filters
        for (const name in regularFilters) {
            const data = regularFilters[name];

            const chip = document.createElement('div');
            chip.className = 'filter-chip';

            // Format values based on filter type
            let displayValues = data.values;

            // Format price values with dollar signs if needed
            if (name === 'ss_price') {
                displayValues = displayValues.map(val => `$${val}`);
            }

            chip.innerHTML = `
                <span>${displayValues.join(', ')}</span>
                <button aria-label="Remove filter" class="filter-chip-remove">&times;</button>
            `;

            // Add click handler to remove all filters for this name
            chip.querySelector('.filter-chip-remove').addEventListener('click', () => {
                removeFiltersFromUrl(data.filters);
            });

            container.appendChild(chip);
        }

        // Create a separate chip for each complete range pair
        rangePairs.forEach(range => {
            const chip = document.createElement('div');
            chip.className = 'filter-chip';

            // Only show ranges with both low and high values
            if (!range.low || !range.high) return;

            // Format range text
            let rangeText = '';
            const isPriceFilter = range.name === 'ss_price';

            if (isPriceFilter) {
                rangeText = `$${range.low} - $${range.high}`;
            } else {
                rangeText = `${range.low} - ${range.high}`;
            }

            chip.innerHTML = `
                <span>${rangeText}</span>
                <button aria-label="Remove filter" class="filter-chip-remove">&times;</button>
            `;

            // Add click handler to remove this specific range
            chip.querySelector('.filter-chip-remove').addEventListener('click', () => {
                removeFiltersFromUrl(range.filters);
            });

            container.appendChild(chip);
        });
    }

    function removeFiltersFromUrl(filtersToRemove) {
        const url = new URL(window.location.href);
        const filtersParam = url.searchParams.get('filters') || '';

        if (!filtersParam) return;

        // Get all filters and filter out the ones to remove
        let filterArray = filtersParam.split(',');
        filterArray = filterArray.filter(filter => !filtersToRemove.includes(filter));

        // Update URL
        if (filterArray.length > 0) {
            url.searchParams.set('filters', filterArray.join(','));
        } else {
            url.searchParams.delete('filters');
        }

        // Update browser history
        window.history.replaceState({}, '', url.toString());

        // Update filter chips immediately
        updateFilterChips();

        // Update search params and perform search
        reloadAllCollections();
    }

    function setupScrollSpy() {
        const navBoxes = document.querySelectorAll('.collection-nav-box');
        const contentSections = document.querySelectorAll('.collection-content-section');

        // Track which sections are currently intersecting
        const intersectingSections = new Set();

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const sectionIndex = entry.target.getAttribute('data-collection-index');

                if (entry.isIntersecting) {
                    intersectingSections.add(sectionIndex);
                } else {
                    intersectingSections.delete(sectionIndex);
                }
            });

            // Update active nav based on the first intersecting section
            if (intersectingSections.size > 0) {
                const firstIntersectingIndex = Array.from(intersectingSections).sort((a, b) => parseInt(a) - parseInt(b))[0];

                navBoxes.forEach(box => box.classList.remove('active'));
                const activeNavBox = document.querySelector(`[data-collection-index="${firstIntersectingIndex}"]`);
                if (activeNavBox) {
                    activeNavBox.classList.add('active');
                }
            }
        }, {
            threshold: 0.1, // Lower threshold for better detection
            rootMargin: '-10% 0px -10% 0px' // Less aggressive margins
        });

        contentSections.forEach(section => {
            observer.observe(section);
        });

        // Add scroll event listener as fallback for edge cases
        let scrollTimeout;
        window.addEventListener('scroll', () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                // If no sections are intersecting, find the closest one
                if (intersectingSections.size === 0) {
                    const scrollPosition = window.scrollY + window.innerHeight * 0.3;
                    let closestSection = null;
                    let closestDistance = Infinity;

                    contentSections.forEach(section => {
                        const rect = section.getBoundingClientRect();
                        const sectionTop = rect.top + window.scrollY;
                        const distance = Math.abs(sectionTop - scrollPosition);

                        if (distance < closestDistance) {
                            closestDistance = distance;
                            closestSection = section;
                        }
                    });

                    if (closestSection) {
                        const sectionIndex = closestSection.getAttribute('data-collection-index');
                        navBoxes.forEach(box => box.classList.remove('active'));
                        const activeNavBox = document.querySelector(`[data-collection-index="${sectionIndex}"]`);
                        if (activeNavBox) {
                            activeNavBox.classList.add('active');
                        }
                    }
                }
            }, 100);
        });
    }

    async function loadCollectionProducts(collectionHandle, collectionIndex) {
        const productGridId = `visual-collection-${collectionHandle}-${collectionIndex}`;
        const productGrid = document.getElementById(productGridId);

        if (!productGrid) return;

        // Check if products are already loaded
        if (productGrid.children.length > 0) return;

        const productCards = new ProductCards(productGridId);

        // Show loading skeletons
        productCards.renderSkeletons(24);

        const searchParams = {
            ...defaultParams,
            'bgfilter.collection_handle': collectionHandle,
            'bgfilter.status': 'active'
        };

        // Apply filters from URL to search params
        applyFiltersFromUrl(searchParams);

        const queryString = objectToUrlParams(searchParams);

        // Build the SearchSpring API URL
        const apiUrl = `https://${SITE_ID}.a.searchspring.io/api/search/search.json?${queryString}`;

        try {
            // Send preflight request first
            await sendPreflight();

            // Make the GET request to the SearchSpring API
            const response = await fetch(apiUrl);

            // Handle redirects
            if (response.redirected) {
                window.location.href = response.url;
                return;
            }

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();

            const filteredResults = await filterProductsByEarlyDrop(data.results);

            // Handle redirect from the response data
            if (data?.merchandising?.redirect) {
                window.location.href = data.merchandising.redirect;
                return;
            }

            // Store collection data for facet merging
            allCollectionData.push(data);

            // Process products with swatches
            const productsWithSwatches = await addSwatchesToProducts(filteredResults);

            // Add inline merchandising to results
            if (data.merchandising?.content?.inline?.length > 0) {
                data.merchandising.content.inline.forEach(item => {
                    const content = item.value;
                    const {index} = item.config?.position || 0;
                    // only show the inline banner if its index falls within the current page
                    if (index >= (searchParams.page * searchParams.resultsPerPage - searchParams.resultsPerPage) && index < (searchParams.page * searchParams.resultsPerPage)) {
                        productsWithSwatches.splice(index, 0, {
                            content,
                            isMerchandising: true,
                        });
                    }
                });
            }

            // Render the products
            productCards.render(productsWithSwatches, true);

            // Handle merchandising content
            if (Object.keys(data?.merchandising?.content || {}).length > 0) {
                processMerchandisingContent(data.merchandising.content, collectionIndex);
            }

        } catch (error) {
            // Don't log errors for intentional redirects
            if (!error.message.startsWith('Redirecting to')) {
                console.error('Error fetching collection products:', error);
            }
        }
    }

    function applyFiltersFromUrl(searchParams) {
        const urlParams = new URLSearchParams(window.location.search);
        const filters = urlParams.get('filters') || '';

        if (!filters) return; // No filters to apply

        // Clear all existing filter parameters before applying new ones
        Object.keys(searchParams).forEach(key => {
            if (key.startsWith('filter.')) {
                delete searchParams[key];
            }
        });

        const filtersArray = filters.split(',');
        filtersArray.forEach(filter => {
            const [key, value] = filter.split(':');

            // Check if this is a range filter (ends with .low or .high)
            const isRangeFilter = key.endsWith('.low') || key.endsWith('.high');

            if (isRangeFilter) {
                // For range filters, we just add them directly to the params
                if (searchParams[key] !== undefined) {
                    if (Array.isArray(searchParams[key])) {
                        searchParams[key].push(value);
                    } else {
                        searchParams[key] = [searchParams[key], value];
                    }
                } else {
                    searchParams[key] = value;
                }
            } else {
                // Regular filters with possible multiple values
                value.split('|').forEach(v => {
                    if (searchParams[key] !== undefined) {
                        if (Array.isArray(searchParams[key])) {
                            searchParams[key].push(v);
                        } else {
                            searchParams[key] = [searchParams[key], v];
                        }
                    } else {
                        searchParams[key] = v;
                    }
                });
            }
        });
    }

    function processMerchandisingContent(merchandisingContent, collectionIndex) {
        Object.keys(merchandisingContent).forEach(key => {
            const targetElement = document.getElementById(`ss-merchandising-${key}-${collectionIndex}`);

            if (!targetElement || !Array.isArray(merchandisingContent[key])) return;

            // Clear any existing content
            targetElement.innerHTML = '';

            // Process each HTML element for this key
            merchandisingContent[key].forEach(htmlContent => {
                // Check if content is wrapped in script tags and remove them
                const scriptRegex = /^<script[^>]*>([\s\S]*)<\/script>$/i;
                const match = scriptRegex.exec(htmlContent);
                const cleanContent = match && match[1] ? match[1].trim() : htmlContent;

                // Append the cleaned content
                targetElement.innerHTML += cleanContent;
            });
        });
    }
});